import { ref, computed, toValue, type MaybeRefOrGetter, type Ref } from 'vue';
import type { ModelType } from '@/models';

export interface UseModelOptions {
  /**
   * Accepts a plain object, a ref or a getter. Pass a ref/getter when the filters can change
   * after the composable is created — a plain object is read once and never revisited.
   */
  baseFilters?: MaybeRefOrGetter<Record<string, any>>;
  includes?: string[];
  search?: Ref<string>;
  filters?: Ref<Record<string, any>>;
}

export function useModel(model: ModelType, options: UseModelOptions = {}) {
  const {
    baseFilters = {},
    includes = [],
    search = ref(''),
    filters = ref({})
  } = options;

  const getModelQuery = computed(() => {
    // Start from an instance: the bare class has no instance methods (exportUrl, exportColumnsUrl…)
    // and would be returned as-is when no filter, include or search applies.
    let modelQuery = model.instance();

    // Apply base filters and dynamic filters.
    // toValue() is read inside the computed so a ref/getter source stays tracked.
    Object.entries({
      ...toValue(baseFilters),
      ...filters.value,
    }).forEach(filter => {
      if (Array.isArray(filter[1])) {
        modelQuery = modelQuery.whereIn(filter[0], filter[1].map((v) => v.toString().trim()));
      } else if (filter[1]?.toString().trim()) {
        modelQuery = modelQuery.where(filter[0], filter[1]?.toString().trim());
      }
    });

    // Apply includes (pass all at once — vue-api-query's .include() replaces, not appends)
    if (includes.length > 0) {
      modelQuery = modelQuery.include(...includes);
    }

    // Apply search
    if (search.value) {
      modelQuery = modelQuery.params({
        search: search.value,
      });
    }

    return modelQuery;
  });

  const executeQuery = async (queryOptions: {
    page?: number;
    limit?: number;
    sort?: string;
    sortDirection?: 'asc' | 'desc';
  } = {}) => {
    const {
      page = 1,
      limit = 20,
      sort = '',
      sortDirection = 'asc'
    } = queryOptions;

    let query = getModelQuery.value;

    if (page) {
      query = query.page(page);
    }

    if (limit) {
      query = query.limit(limit);
    }

    if (sort) {
      const orderBy = (sortDirection === 'asc' ? '' : '-') + sort;
      query = query.orderBy(orderBy);
    }

    return await query.get();
  };

  return {
    getModelQuery,
    executeQuery
  };
}
